import get from "lodash/get";

import { useContext } from "react";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import ProductOptionSelector from "@components/store/product-detail/shared/product-option-selector";
import PropertiesAccordionList from "@components/store/product-detail/shared/properties-accordion-list";
import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";
import VendorHolidayMessage from "@components/store/product-detail/shared/vendor-holiday-message";
import ProductNotAvailableMessage from "@components/store/product-detail/shared/product-not-available-message";
import ProductInfoSection from "@components/store/product-detail/shared/product-info-section";
import ProductDescriptionAccordion from "@components/store/product-detail/shared/accordion/product-description";
import { getProductType, getProductCategory } from "@helpers/products/types";

import useShowPrefrenceRestrictionModal from "@hooks/product-detail/use-show-prefrence-restriction-modal";

import { useProductInfoContext } from "@contexts/product-info-context";

import { OPTIONTYPE_PACK_KEY, PACK_OF_ONE_OPTION_TYPE_NAME } from "@constants/products/detail";
import { ANALYTICS_ATTRIBUTE_TYPE } from "@constants/analytics/shop";

import styles from "./main.module.scss";

const SnacksDetails = ({
  product,
  variantValues,
  optionTypeVariantMapping,
  setOptionTypeVariantMapping,
  productQuantity,
  setProductQuantity,
  canBuyProduct,
  renderProductDetailHeader,
}) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { name, dietaryRestrictions, ingredients, vendorByCountry } = product;

  const {
    hideVariantSelectionSection,
    showAddToBox,
    isViewOnlyMode,
    renderCatalogModeFooterLinks,
    renderCustomVariantSelectionSection,
    onSuccessCallback,
  } = useProductInfoContext();

  const { renderRestrictionsModal, renderAddToBagBtn } = useShowPrefrenceRestrictionModal({
    product,
    selectedVariant: optionTypeVariantMapping,
    isBYOProduct: showAddToBox,
  });

  const getMeasurmentUnitInfo = () => {
    const { optionTypeKey, name, weight, measurementUnit } = optionTypeVariantMapping;
    if (optionTypeKey === OPTIONTYPE_PACK_KEY && weight && measurementUnit) {
      const weightInfo = `${weight} ${measurementUnit}`;
      return String(name) === PACK_OF_ONE_OPTION_TYPE_NAME ? weightInfo : `${name} X ${weightInfo}`;
    }
  };

  const handleQuantity = (newQuantity) => {
    setProductQuantity(newQuantity);
  };

  const renderPriceVariants = () => {
    const title = get(variantValues[0], "optionTypePresentation", "Box Size");
    return (
      <ProductOptionSelector
        title={title}
        availData={variantValues}
        selectedData={optionTypeVariantMapping}
        handleChange={setOptionTypeVariantMapping}
        intent={product.intent}
        disabled={!canBuyProduct}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            attribute_type: ANALYTICS_ATTRIBUTE_TYPE.BOX_SIZE,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
      />
    );
  };

  const renderQuantitySelector = () => (
    <div className={styles.qtyColLeft}>
      <span className={styles.qtyWrapper}>Quantity</span>
      <QuantitySelector
        quantity={productQuantity}
        setQuantity={setProductQuantity}
        className="defaultStyle"
        disabled={!canBuyProduct}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
      />
    </div>
  );

  const addToBagProps = {
    product: product,
    quantity: productQuantity,
    variant: optionTypeVariantMapping,
    btnParentClass: styles.bagBtnBlock,
    showAddToBox,
    onSuccessCallback,
    handleQuantity,
    isDisabled: !canBuyProduct,
    restrictions: dietaryRestrictions,
  };

  return (
    <div className={`${styles.boxContainerFluid} boxContainerFluid`}>
      {!isMobileDevice && renderProductDetailHeader()}
      <VendorHolidayMessage vendor={vendorByCountry} />
      {!hideVariantSelectionSection && renderPriceVariants()}
      <ProductNotAvailableMessage canBuyProduct={canBuyProduct} name={name} />
      {!(hideVariantSelectionSection || isViewOnlyMode) && (
        <div className={`${styles.qtyWithBagBtnRow} stickyDemoMode`}>
          {renderQuantitySelector()}
          {renderAddToBagBtn(addToBagProps)}
        </div>
      )}
      {hideVariantSelectionSection && renderCustomVariantSelectionSection()}
      <ProductDescriptionAccordion product={product} />
      <PropertiesAccordionList
        restrictions={dietaryRestrictions}
        ingredients={ingredients}
        instructions={product?.productDetail?.orderInstructions}
        productId={product.id}
      />
      <ProductInfoSection product={product} measurementInfo={getMeasurmentUnitInfo()} />
      {!hideVariantSelectionSection &&
        renderRestrictionsModal({
          product,
          quantity: productQuantity,
          variant: optionTypeVariantMapping,
        })}
      {renderCatalogModeFooterLinks()}
    </div>
  );
};
export default SnacksDetails;
